/**
 * Bounded-concurrency request queue for opener WRITE/activation requests.
 *
 * WHY: when the operator arms many opener legs (rows) in quick succession the
 * FE used to fire every activation/toggle mutation CONCURRENTLY. That burst
 * saturates the single-threaded Node event loop on the position-opener pod —
 * its `/health` can't answer within the liveness window, K8s SIGKILLs the pod
 * (~60s of failed probes), and every in-flight activation fails with the
 * "Failed to change the opener rule — it may still be ACTIVE" toast. Retrying
 * those failed toasts piles on MORE concurrent requests and prolongs the
 * saturation.
 *
 * This is a dependency-free promise pool: `enqueue(fn)` runs at most
 * `maxConcurrency` tasks at once, draining the rest in strict FIFO order. A
 * rejecting task settles the caller's promise but does NOT stall the queue —
 * the freed slot is immediately handed to the next waiter.
 */
export class RequestQueue {
  private readonly maxConcurrency: number;
  private active = 0;
  private readonly waiting: Array<() => void> = [];

  constructor(maxConcurrency: number) {
    // Guard against a misconfigured 0/negative budget deadlocking the queue.
    this.maxConcurrency = Math.max(1, Math.floor(maxConcurrency));
  }

  /** Number of tasks currently executing (for tests / introspection). */
  get activeCount(): number {
    return this.active;
  }

  /** Number of tasks queued but not yet started. */
  get pendingCount(): number {
    return this.waiting.length;
  }

  /**
   * Run `fn` when a concurrency slot is free, preserving FIFO order. Resolves
   * / rejects with whatever `fn` resolves / rejects. The slot is always
   * released on settle, so a rejecting task never strands capacity.
   */
  async enqueue<T>(fn: () => Promise<T>): Promise<T> {
    await this.acquire();
    try {
      return await fn();
    } finally {
      this.release();
    }
  }

  private acquire(): Promise<void> {
    if (this.active < this.maxConcurrency) {
      this.active += 1;
      return Promise.resolve();
    }
    return new Promise<void>(resolve => {
      this.waiting.push(() => {
        this.active += 1;
        resolve();
      });
    });
  }

  private release(): void {
    this.active -= 1;
    const next = this.waiting.shift();
    if (next) next();
  }
}

/**
 * Max opener WRITE requests in flight at once. Tuned low (3) so a mass-arm or
 * a retry-storm can never saturate the single-threaded position-opener event
 * loop and trip its liveness probe. CHANGE HERE to retune the budget.
 */
export const OPENER_WRITE_MAX_CONCURRENCY = 3;

/**
 * Shared module-level queue instance. ALL opener write mutations across every
 * row import this one instance, so the whole page shares a single concurrency
 * budget (per-row queues would each get their own budget and defeat the point).
 */
export const openerWriteQueue = new RequestQueue(OPENER_WRITE_MAX_CONCURRENCY);

/** Convenience wrapper bound to the shared opener-write queue. */
export function enqueueOpenerWrite<T>(fn: () => Promise<T>): Promise<T> {
  return openerWriteQueue.enqueue(fn);
}
